import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bus, RefreshCw, Activity, Wifi, WifiOff, User, Calendar, CheckCircle2, AlertCircle } from "lucide-react";
import { PageShell, PageHeader, StatCard, StatGrid, ModernTable, ActionButton } from "@/components/ds";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

/**
 * Date range helper — shared snippet across the 3 dashboards.
 * Default window: last 30 days.
 */
function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(start), to: iso(end) };
}

const STATUS_LABEL = {
  active: "Actif",
  maintenance: "Maintenance",
  out_of_service: "Hors service",
  inactive: "Inactif",
};

export default function DashboardBus() {
  const [range, setRange] = useState(defaultRange);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dashboards/bus?from_date=${range.from}&to_date=${range.to}`);
      setData(r.data);
    } catch (e) {
      toast.error("Erreur de chargement du tableau de bord Bus");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const chart = data?.chart_top || [];

  const completionPct = useMemo(() => {
    if (totals.completion_rate == null) return "—";
    return `${Math.round((totals.completion_rate || 0) * 100)}%`;
  }, [totals.completion_rate]);

  return (
    <PageShell testId="dashboard-bus">
      <PageHeader
        eyebrow="ANALYTICS"
        title="Dashboard Bus"
        subtitle="Vue d'ensemble de la flotte : configuration permanente, GPS, charge de missions."
        actions={
          <div className="flex items-center gap-2" data-testid="dashboard-bus-controls">
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Du</label>
            <input
              type="date" value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="border border-zinc-900 px-2 py-1 text-sm font-mono"
              data-testid="dashboard-bus-from"
            />
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">au</label>
            <input
              type="date" value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="border border-zinc-900 px-2 py-1 text-sm font-mono"
              data-testid="dashboard-bus-to"
            />
            <ActionButton onClick={load} icon={RefreshCw} testId="dashboard-bus-refresh" disabled={loading}>
              Rafraîchir
            </ActionButton>
          </div>
        }
      />

      <StatGrid cols={4} testId="dashboard-bus-kpis">
        <StatCard icon={Bus} label="BUS TOTAL" value={totals.vehicles ?? "—"} testId="kpi-bus-total" />
        <StatCard icon={CheckCircle2} label="ACTIFS" value={totals.active ?? "—"}
          hint={`${totals.maintenance || 0} maint. • ${totals.out_of_service || 0} HS`}
          tone="success" testId="kpi-bus-active"
        />
        <StatCard icon={Wifi} label="GPS OK" value={totals.with_gps ?? "—"}
          hint={`${(totals.vehicles || 0) - (totals.with_gps || 0)} sans GPS`}
          tone={totals.with_gps === totals.vehicles ? "success" : "warning"}
          testId="kpi-bus-gps"
        />
        <StatCard icon={User} label="AVEC CHAUFFEUR" value={totals.with_primary_driver ?? "—"}
          hint={`${(totals.vehicles || 0) - (totals.with_primary_driver || 0)} sans driver`}
          tone={totals.with_primary_driver === totals.vehicles ? "success" : "warning"}
          testId="kpi-bus-driver"
        />
        <StatCard icon={Calendar} label="MISSIONS" value={totals.missions_total ?? 0}
          accent testId="kpi-bus-missions"
        />
        <StatCard icon={CheckCircle2} label="TERMINÉES" value={totals.missions_completed ?? 0}
          hint={completionPct} tone="success" testId="kpi-bus-completed"
        />
        <StatCard icon={Activity} label="EN COURS / PENDING" value={totals.missions_pending ?? 0}
          testId="kpi-bus-pending"
        />
        <StatCard icon={AlertCircle} label="SANS AFFECTATION" value={totals.missions_pending_assignment ?? 0}
          tone={(totals.missions_pending_assignment || 0) > 0 ? "warning" : "default"}
          testId="kpi-bus-pending-assignment"
        />
      </StatGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 border border-zinc-900 bg-white p-5" data-testid="dashboard-bus-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-black uppercase text-lg tracking-tight">Top 10 — Missions par bus</h3>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {range.from} → {range.to}
            </span>
          </div>
          {chart.length === 0 ? (
            <div className="text-center text-zinc-500 text-xs py-12">Aucune mission sur la période</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "#FFF7CC" }} />
                <Bar dataKey="missions" fill="#FFC60A" stroke="#18181b" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-zinc-900 bg-zinc-900 text-white p-5" data-testid="dashboard-bus-summary">
          <h3 className="font-display font-black uppercase text-lg tracking-tight mb-4">État flotte</h3>
          <dl className="space-y-3 text-sm">
            {Object.entries(STATUS_LABEL).map(([k, lbl]) => (
              <div key={k} className="flex items-baseline justify-between border-b border-zinc-700 pb-2">
                <dt className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{lbl}</dt>
                <dd className="font-display font-black text-2xl">{totals[k] ?? 0}</dd>
              </div>
            ))}
            <div className="flex items-baseline justify-between pt-2">
              <dt className="text-[11px] font-mono uppercase tracking-widest text-[#FFC60A]">
                Tournée principale
              </dt>
              <dd className="font-display font-black text-2xl text-[#FFC60A]">
                {totals.with_primary_tour ?? 0}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-display font-black uppercase text-lg tracking-tight mb-3">Détail par bus</h3>
        <ModernTable
          testId="dashboard-bus-table"
          stickyHeader
          columns={[
            { key: "label", label: "Bus" },
            { key: "status", label: "Statut" },
            { key: "gps_ok", label: "GPS", align: "right" },
            { key: "primary_driver_name", label: "Chauffeur principal" },
            { key: "primary_client_name", label: "Client" },
            { key: "missions_total", label: "Missions", align: "right" },
            { key: "missions_completed", label: "Terminées", align: "right" },
            { key: "missions_pending", label: "Pending", align: "right" },
            { key: "completion_rate", label: "% Compl.", align: "right" },
          ]}
          rows={rows}
          rowKey="id"
          renderCell={(row, col) => {
            switch (col.key) {
              case "label":
                return (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 bg-zinc-900 text-[#FFC60A] font-display font-black text-xs">
                      {String(row.plate || "").slice(0, 3)}
                    </span>
                    <span className="font-medium" data-testid={`dashboard-bus-row-${row.plate}`}>{row.label}</span>
                  </div>
                );
              case "status":
                return <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${row.status === "active" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>{STATUS_LABEL[row.status] || row.status}</span>;
              case "gps_ok":
                return row.gps_ok
                  ? <Wifi size={14} className="inline text-emerald-600" />
                  : <WifiOff size={14} className="inline text-zinc-400" />;
              case "primary_driver_name":
                return row.primary_driver_name || <span className="text-zinc-400">—</span>;
              case "primary_client_name":
                return row.primary_client_name || <span className="text-zinc-400">—</span>;
              case "completion_rate":
                return row.completion_rate == null
                  ? <span className="text-zinc-400">—</span>
                  : `${Math.round(row.completion_rate * 100)}%`;
              default:
                return row[col.key] ?? <span className="text-zinc-400">—</span>;
            }
          }}
          emptyMessage={loading ? "Chargement…" : "Aucun bus."}
        />
      </div>
    </PageShell>
  );
}
